import React, { useEffect, useState } from "react";
import {
  FaSpinner,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaBell,
  FaAlignLeft,
} from "react-icons/fa";
import {
  getSpecificUserReminders,
  careateUserReminder,
  deleteSpecificReminder,
  updateUserReminder,
} from "../../../services/reminder.services";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

function RemindersTab({ fetchLeads }) {
  const [reminders, setReminders] = useState([]);
  const [reminder, setReminder] = useState({
    reminder: "",
    trigger_date: "",
    trigger_time: "",
  });
  const selectedLead = useSelector((state) => state.leads.selectedLead);
  const [loading, setLoading] = useState(false);
  const [fetchingReminders, setFetchingReminders] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);

  useEffect(() => {
    if (selectedLead?.id) {
      fetchReminders();
    }
  }, [selectedLead]);

  const fetchReminders = async () => {
    setFetchingReminders(true);
    try {
      const res = await getSpecificUserReminders(selectedLead.id);
      setReminders(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
      setReminders([]);
    } finally {
      setFetchingReminders(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReminder({ ...reminder, [name]: value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!reminder.reminder.trim()) {
      toast.error("Please enter a reminder description");
      return;
    }

    setLoading(true);
    try {
      // Format time properly
      const timeValue = reminder.trigger_time;
      let hours, minutes, period;

      // First try to handle input field format
      const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);

      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = timeMatch[2];
        period = timeMatch[3]?.toUpperCase() || "";

        if (period === "PM" && hours < 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }
      } else {
        throw new Error("Invalid time format");
      }

      const triggerTime = `${hours.toString().padStart(2, "0")}:${minutes}`;

      if (editingReminderId) {
        // Update existing reminder
        const updatedData = {
          ...reminder,
          trigger_time: triggerTime,
        };

        await updateUserReminder(editingReminderId, updatedData);
        toast.success("Reminder updated successfully!");
      } else {
        // Create new reminder
        const reminderData = {
          ...reminder,
          trigger_time: triggerTime,
          lead: selectedLead.id,
        };

        await careateUserReminder(reminderData);
        toast.success("Reminder set successfully!");
      }

      // Reset form and refresh data
      setReminder({ reminder: "", trigger_date: "", trigger_time: "" });
      setEditingReminderId(null);
      await fetchReminders();
    } catch (error) {
      console.error("Reminder operation failed:", error);
      toast.error(error.message || "Failed to process reminder");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (id) => {
    if (window.confirm("Are you sure you want to delete this reminder?")) {
      try {
        setLoading(true);
        await deleteSpecificReminder(id);
        toast.success("Reminder deleted successfully!");
        await fetchReminders();
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete reminder");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditReminder = (reminder) => {
    // Format time for the input field
    // Convert 24h format to 12h format with AM/PM
    let formattedTime = reminder.trigger_time;
    if (reminder.trigger_time) {
      const [hours, minutes] = reminder.trigger_time.split(":");
      const hour = parseInt(hours, 10);
      const minute = minutes;
      const period = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      formattedTime = `${formattedHour}:${minute} ${period}`;
    }

    setReminder({
      reminder: reminder.reminder,
      trigger_date: reminder.trigger_date,
      trigger_time: formattedTime,
    });
    setEditingReminderId(reminder.id);
  };

  const handleCancelEdit = () => {
    setEditingReminderId(null);
    setReminder({ reminder: "", trigger_date: "", trigger_time: "" });
  };

  const generateTimeOptions = () => {
    const options = [];
    const periods = ["AM", "PM"];

    for (let period of periods) {
      for (let hour = 1; hour <= 12; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString();
          const formattedMinute = minute.toString().padStart(2, "0");
          options.push(`${formattedHour}:${formattedMinute} ${period}`);
        }
      }
    }

    return options;
  };

  const timeOptions = generateTimeOptions();

  // Format date in a more readable way
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return "";

    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const minute = minutes;
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${formattedHour}:${minute} ${period}`;
  };

  // Check if a reminder is overdue
  const isOverdue = (dateString, timeString) => {
    if (!dateString || !timeString) return false;

    const now = new Date();
    const [hours, minutes] = timeString.split(":");
    const reminderDate = new Date(dateString);
    reminderDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));

    return reminderDate < now;
  };

  return (
    <div className="reminders-tab h-full flex flex-col bg-white overflow-hidden">
      {/* Form Section - Compact Design */}
      <div className="flex-shrink-0 p-3 border-b border-gray-200">
        <h2 className="text-lg font-bold mb-3 text-gray-800 flex items-center">
          <FaBell className="mr-2 text-indigo-600" />
          {editingReminderId ? "Edit Reminder" : "Set a Reminder"}
        </h2>

        <form onSubmit={handleFormSubmit} className="space-y-3">
          {/* Description - Compact */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
              <FaAlignLeft className="mr-1 text-gray-500 text-xs" />
              Description
            </label>
            <textarea
              name="reminder"
              value={reminder.reminder}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 rounded-md p-2 text-sm transition-all focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              placeholder="Enter reminder details"
              required
              disabled={loading}
            />
          </div>

          {/* Date and Time - Inline on larger screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                <FaCalendarAlt className="mr-1 text-gray-500 text-xs" />
                Date
              </label>
              <input
                type="date"
                name="trigger_date"
                value={reminder.trigger_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2 text-sm transition-all focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
                disabled={loading}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                <FaClock className="mr-1 text-gray-500 text-xs" />
                Time
              </label>
              <input
                list="timeOptions"
                name="trigger_time"
                value={reminder.trigger_time}
                onChange={handleChange}
                placeholder="e.g., 9:30 AM"
                className="w-full border border-gray-300 rounded-md p-2 text-sm transition-all focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
                disabled={loading}
              />
              <datalist id="timeOptions">
                {timeOptions.map((time) => (
                  <option key={time} value={time} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Action Buttons - Compact */}
          <div className="flex items-center justify-between pt-2">
            {editingReminderId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 transition-colors flex items-center text-sm"
                disabled={loading}
              >
                <FaTimes className="mr-1 text-xs" />
                Cancel
              </button>
            )}

            <button
              type="submit"
              className={`${
                editingReminderId ? "" : "ml-auto"
              } px-4 py-1.5 rounded-md text-white font-medium transition-colors flex items-center justify-center text-sm ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
              disabled={loading}
            >
              {loading ? (
                <FaSpinner className="animate-spin mr-1 text-xs" />
              ) : editingReminderId ? (
                <FaSave className="mr-1 text-xs" />
              ) : (
                <FaBell className="mr-1 text-xs" />
              )}
              {loading
                ? "Processing..."
                : editingReminderId
                ? "Update"
                : "Set Reminder"}
            </button>
          </div>
        </form>
      </div>

      {/* Reminders List - Flexible Height */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-shrink-0 p-3 pb-2 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800 flex items-center">
            <FaCalendarAlt className="mr-2 text-indigo-600" />
            Upcoming Reminders
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {fetchingReminders ? (
            <div className="flex justify-center items-center py-8">
              <FaSpinner className="animate-spin text-2xl text-indigo-600" />
            </div>
          ) : (
            <div className="space-y-2">
              {reminders.length > 0 ? (
                reminders.reverse().map((reminder) => {
                  const overdueStatus = isOverdue(
                    reminder.trigger_date,
                    reminder.trigger_time
                  );

                  return (
                    <div
                      key={reminder.id}
                      className={`rounded-lg p-3 border-l-4 transition-all ${
                        overdueStatus
                          ? "bg-red-50 border-red-500"
                          : "bg-indigo-50 border-indigo-500"
                      }`}
                    >
                      <p className="text-gray-800 font-medium text-sm mb-2">
                        {reminder.reminder}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                        <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-3 text-xs">
                          <span
                            className={`flex items-center ${
                              overdueStatus ? "text-red-600" : "text-indigo-600"
                            }`}
                          >
                            <FaCalendarAlt className="mr-1" />
                            {formatDate(reminder.trigger_date)}
                          </span>
                          <span
                            className={`flex items-center ${
                              overdueStatus ? "text-red-600" : "text-indigo-600"
                            }`}
                          >
                            <FaClock className="mr-1" />
                            {formatTime(reminder.trigger_time)}
                          </span>
                          {overdueStatus && (
                            <span className="text-red-600 font-medium">
                              Overdue
                            </span>
                          )}
                        </div>

                        <div className="flex space-x-2 mt-2 sm:mt-0">
                          <button
                            className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-100 transition-colors"
                            onClick={() => handleEditReminder(reminder)}
                            title="Edit Reminder"
                            disabled={loading}
                          >
                            <FaEdit className="text-xs" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => handleDeleteReminder(reminder.id)}
                            title="Delete Reminder"
                            disabled={loading}
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FaBell className="mx-auto text-3xl text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No reminders set.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Set a reminder to get notified about important dates and
                    tasks.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RemindersTab;
